import React, { useState } from 'react';
import { useNavigate, useLocation, NavigateFunction, Location } from 'react-router-dom';
import { styled } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useGetCurrentUserQuery } from 'src/redux/services/users';
import LoginLayout from 'src/components/Layouts/LoginLayout';
import LoadingScreen from 'src/frontend-common/components/LoadingScreen';
import { User } from 'src/dto/User';
import { setAccessToken } from 'src/frontend-common/localstorage';
interface PropTypes {
  adminOnly?: boolean;
  children: React.JSX.Element | React.JSX.Element[];
}

interface TokenFlowOptions {
  navigate?: NavigateFunction;
  location?: Location;
  tokenInQuery?: string;
}
const UserGuard: React.FC<PropTypes> = ({ children, adminOnly }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryAccessTokenNew = new URLSearchParams(useLocation().hash?.substring(1)).get('token');
  const [user, setUser] = useState<User | undefined>(undefined);
  const { data, isSuccess, isError, error } = useGetCurrentUserQuery();

  React.useEffect(() => {
    handleTokenFlow({ navigate, location, tokenInQuery: queryAccessTokenNew || undefined })();
  }, []);

  React.useEffect(() => {
    if (isError && error && 'status' in error) {
      if (error.status === 401) {
        navigate('/login');
      }
    }

    if (isSuccess && data?.item) {
      if (data.item.incomplete) {
        navigate('/complete');
      }

      if (!data.item.incomplete) {
        setUser(data.item);
        Sentry.setUser({ email: data.item.email, username: data.item.username });
      }
    }
  }, [data, isSuccess, isError, error]);

  return (
    <>
      {user ? (
        <LoginLayout user={user} adminOnly={adminOnly}>
          <>{children}</>
        </LoginLayout>
      ) : (
        <LoadingScreenWrapper />
      )}
    </>
  );
};

export default UserGuard;

const handleTokenFlow = (options?: TokenFlowOptions): (() => Promise<void>) => {
  return async () => {
    const { navigate, location, tokenInQuery } = options || {};

    if (tokenInQuery) {
      setAccessToken(tokenInQuery);

      if (navigate && location) {
        // Replace current entry in history stack
        navigate(location.pathname, { replace: true });
      }
      return;
    }
  };
};

const LoadingScreenWrapper = styled(LoadingScreen)(({ theme }) => ({
  minHeight: '500px',
  backgroundColor: theme.palette.background.paper,
}));
